import {
	type UserInstrumentImportResponse,
	type UserInstrumentDto,
	InstrumentsControllerApiFactory,
	IntegrationsControllerV3ApiFactory,
	SelectableBasketBasketTypeEnum,
} from "@eu.mdotm.api/sphere";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { DropzoneArea } from "$root/components/DropzoneArea";
import { PageHeader } from "$root/components/PageHeader";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import UploadButton from "$root/components/UploadButton";
import EmptyImportBox from "$root/functional-areas/instruments-editor/spawn/box/EmptyImportBox";
import ErrorImportBox from "$root/functional-areas/instruments-editor/spawn/box/ErrorImportBox";
import SuccessImportBox from "$root/functional-areas/instruments-editor/spawn/box/SuccessImportBox";
import InstrumentCustomizationtableV3 from "$root/functional-areas/instruments/Instrument-customisation";
import { instrumentCustomisation$ } from "$root/functional-areas/instruments/Instrument-customisation/InstrumentCustomisationStore";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { typedQueryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import { downloadContentDisposition, ToastableError } from "$root/utils";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import {
	ActionText,
	Banner,
	Collapsible,
	DefaultCollapsibleHeader,
	Dialog,
	DialogFooter,
	Icon,
} from "@mdotm/shadcn/legacy/components";
import { type MaybePromise, asyncStateFrom, useAsync } from "@mdotm/utils/promise";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import XLSX from "xlsx";

export default function InstrumentCustomisationSetting({
	universeUuid,
	universeName,
	basketType,
}: {
	universeUuid?: string;
	universeName?: string;
	basketType?: string;
}): React.JSX.Element {
	const crumbs = useCrumbs();
	const firstRun = useRef(true);
	const gridRef = useRef<AgGridReact<UserInstrumentDto>>(null);

	// not pretty but will do for now
	if (firstRun.current && universeUuid && universeName) {
		firstRun.current = false;
		writeAtom(instrumentCustomisation$, (curr) => ({
			...curr,
			search: "",
			selectedfilters: [],
			selectedBaskets: [
				{
					basketIdentifier: universeUuid,
					basketName: universeName,
					basketType:
						(basketType as SelectableBasketBasketTypeEnum | undefined) || SelectableBasketBasketTypeEnum.Universe,
				},
			],
		}));
	}

	return (
		<>
			<PageHeader
				crumbs={crumbs.mySettings("InstrumentCustomisation")}
				titleAction={
					<Button
						size="small"
						palette="primary"
						onClickAsync={async () => {
							if (gridRef.current) {
								const { userInstrumentsColumnPreferences: backendPreferences } =
									await instrumentsRepository.userInstrumentsColumnPreferences.fetchQuery();
								const agGridVisibilityMap = new window.Map(
									gridRef.current.api.getColumnState().map((c) => [c.colId, /* visible */ !c.hide] as const),
								);
								const mergedPreferences = backendPreferences!.map((backend) => ({
									...backend,
									enabled:
										backend.preferenceType!.classificationUuid === "NAME" ||
										agGridVisibilityMap.get(backend.preferenceType!.classificationUuid!),
								}));

								await instrumentsRepository.setUserInstrumentsColumnPreferences(mergedPreferences).catch((err) => {
									console.warn("could not update column preferences in preparation of the template download", err);
								});
							}
							spawnImportUserInstrumentDialog({
								async onImportFinished() {
									await typedQueryClient().invalidateQueries("userInstrumentsBaseQueryKey");
								},
							});
						}}
					>
						<Icon icon="Dowload" size={16} className="-rotate-90 mr-1" />
						Import
					</Button>
				}
			/>
			<InstrumentCustomizationtableV3 gridRef={gridRef} />
		</>
	);
}

type ImportUserInstrumentDialogProps = MinimumDialogProps & {
	onImportFinished?(): MaybePromise<void>;
};

const ACCEPTED_FILE = "*";

const BoxMap = {
	empty: <EmptyImportBox />,
	error: <ErrorImportBox />,
	success: <SuccessImportBox />,
} as const;

function ImportUserInstrumentDialog({
	show,
	onClose,
	onAnimationStateChange,
	onImportFinished,
}: ImportUserInstrumentDialogProps): React.JSX.Element {
	const { t } = useTranslation();
	const [userInstrument, setUserInstrument] = useState<
		| {
				file?: null;
				conversion?: UserInstrumentImportResponse;
		  }
		| {
				file: File;
				conversion: UserInstrumentImportResponse;
		  }
	>({ file: null });

	const instrumentsControllerApi = useApiGen(InstrumentsControllerApiFactory);
	const integrationsControllerV3Api = useApiGen(IntegrationsControllerV3ApiFactory);

	const handleDownloadTemplate = useCallback(
		() =>
			runWithErrorReporting(
				async () => {
					await integrationsControllerV3Api
						.getUploadTemplate("SPHERE_INSTRUMENT_IMPORT", {
							responseType: "blob",
						})
						.then(downloadContentDisposition);
				},
				{
					area: "instrument-customisation",
					attemptedOperation: {
						message: "try to download instrument template",
					},
				},
			),
		[integrationsControllerV3Api],
	);

	const [handleFileChange, fileChangeState] = useAsync<void, [File | null]>(async (file) => {
		if (!file) {
			setUserInstrument({ file: null });
			return;
		}
		await runWithErrorReporting(
			async () => {
				try {
					const conversion = await axiosExtract(instrumentsControllerApi.importInstruments(file));
					setUserInstrument({
						file,
						conversion,
					});
				} catch (error) {
					setUserInstrument({ file: null });
					throw new Error(String(error));
				}
			},
			{
				area: "instrument-customisation",
				attemptedOperation: {
					message: "try to convert a file",
				},
			},
		);
	});

	const canUpload = useMemo(
		() => (userInstrument.conversion?.userInstrumentImports?.length ?? 0) > 0,
		[userInstrument.conversion?.userInstrumentImports?.length],
	);

	const hasConversionErrorsOrWarnings = useMemo(
		() => (userInstrument.conversion?.errors?.length ?? 0) + (userInstrument.conversion?.warnings?.length ?? 0) > 0,
		[userInstrument.conversion?.errors?.length, userInstrument.conversion?.warnings?.length],
	);

	const handleDownloadErrorReport = useCallback(() => {
		const xlsSheet = [
			...(userInstrument.conversion?.errors?.map(({ errorMessage }) => ({
				File: userInstrument.file?.name ?? "",
				Message: errorMessage,
				Type: "Error",
			})) ?? []),
			...(userInstrument.conversion?.warnings?.map(({ errorMessage }) => ({
				File: userInstrument.file?.name ?? "",
				Message: errorMessage,
				Type: "Warning",
			})) ?? []),
		];

		const reportWS = XLSX.utils.json_to_sheet(xlsSheet);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, reportWS, "Report Errors");
		XLSX.writeFile(wb, "Errors file.xlsx");
	}, [userInstrument.conversion?.errors, userInstrument.conversion?.warnings, userInstrument.file?.name]);

	return (
		<Dialog
			size="large"
			show={show}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			header="Select"
			onSubmitAsync={async () => {
				await runWithErrorReporting(
					async () => {
						if (!userInstrument.conversion?.userInstrumentImports) {
							throw new ToastableError("Missing file", {
								cause: "undefined or null file not valid",
								icon: "Portfolio",
							});
						}

						await instrumentsControllerApi.updateInstrumentsByInputList(
							userInstrument.conversion.userInstrumentImports,
						);

						platformToast({
							icon: "toolchart-table",
							children: "Sphere has taken over your request",
							severity: "success",
						});

						await onImportFinished?.();
					},
					{
						area: "instrument-customisation",
						attemptedOperation: {
							message: "import custom user instruments",
						},
					},
				);
			}}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton palette="primary" disabled={!canUpload}>
							{t("BUTTON.CONTINUE")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<ol className="list-decimal ml-4 mb-4">
				<li>
					Download the <ActionText onClickAsync={handleDownloadTemplate}>template</ActionText>
				</li>
				<li>Insert your instruments and their data</li>
				<li>Upload the edited template. Only files formatted like the template will be accepted.</li>
			</ol>
			<Banner
				overrides={{
					background: "white",
				}}
				severity="info"
				title="Overwrite operation"
				className="mb-4"
			>
				Please note that uploading instruments data will overwrite any existing information.
			</Banner>
			<DropzoneArea
				disabled={fileChangeState.kind === "loading"}
				onChange={unpromisify(async (file: File | null) => {
					await handleFileChange(file);
				})}
				accept={ACCEPTED_FILE}
				childrenWrapperAppearance={{
					className: {
						[`relative rounded-sm flex flex-1 gap-4 p-4 justify-between items-center border-2 mb-4`]: true,
						[`bg-[color:var(--palette-N50)] border-dashed border-[color:var(--palette-N500)]`]: !userInstrument.file,
						[`bg-[color:var(--palette-N0)] border-[color:var(--palette-P600)]`]: Boolean(
							userInstrument.file && canUpload,
						),
						[`bg-[color:var(--palette-N0)] border-[color:var(--palette-D500)]`]:
							hasConversionErrorsOrWarnings && !canUpload,
					},
				}}
			>
				<div className="flex gap-2 items-center flex-1">
					{userInstrument.file ? (
						canUpload ? (
							<>
								{BoxMap.success}
								<p className="font-semibold">{userInstrument.file.name}</p>
							</>
						) : (
							<>
								{BoxMap.error}
								<p className="font-semibold">{userInstrument.file.name}</p>
							</>
						)
					) : (
						<>
							{BoxMap.empty}
							<p className="font-semibold">Select a file or drag and drop here</p>
						</>
					)}
				</div>

				{userInstrument.file ? (
					<Button
						state={asyncStateFrom(fileChangeState.kind === "loading" ? "loading" : "initial")}
						palette="tertiary"
						size="small"
						onClick={() => setUserInstrument({ file: null })}
					>
						<Icon icon="Delete" size={20} color={themeCSSVars.palette_N500} />
					</Button>
				) : (
					<UploadButton
						loading={fileChangeState.kind === "loading"}
						size="small"
						label={t("BUTTON.SELECT")}
						onChange={unpromisify(async (file: File | null) => {
							await handleFileChange(file);
						})}
						accept={ACCEPTED_FILE}
					/>
				)}
			</DropzoneArea>

			{hasConversionErrorsOrWarnings &&
				(canUpload ? (
					<Banner severity="warning" title="Some updates cannot be applied" className="mb-4">
						<p>You can still proceed, but errors will be skipped</p>
						{"\n"}
						<ActionText as="button" onClick={() => handleDownloadErrorReport()}>
							Download issues file
						</ActionText>
					</Banner>
				) : (
					<Banner severity="error" title="It's not possible to proceed with the upload" className="mb-4">
						<p>Please review the file and upload it again</p>
						{"\n"}
						<ActionText as="button" onClick={() => handleDownloadErrorReport()}>
							Download errors file
						</ActionText>
					</Banner>
				))}

			{userInstrument.file && hasConversionErrorsOrWarnings && (
				<Collapsible
					header={(params) => (
						<DefaultCollapsibleHeader
							{...params}
							className={{
								[`bg-transparent! min-h-[40px] flex items-center`]: true,
							}}
						>
							{userInstrument.file.name}
						</DefaultCollapsibleHeader>
					)}
					className="mb-2"
				>
					<ForEach collection={userInstrument.conversion?.errors ?? []}>
						{(e) => (
							<div className="h-[40px] border-b border-[#eeeef1] transition-[background] data-forceHover:bg-[#EFF0F3] hover:bg-[#EFF0F3] flex space-x-2 items-center px-2">
								<p className="p-2">{e.errorMessage}</p>
							</div>
						)}
					</ForEach>
					<ForEach collection={userInstrument.conversion?.warnings ?? []}>
						{(w) => (
							<div className="h-[40px] border-b border-[#eeeef1] transition-[background] data-forceHover:bg-[#EFF0F3] hover:bg-[#EFF0F3] flex space-x-2 items-center px-2">
								<p className="p-2">{w.errorMessage}</p>
							</div>
						)}
					</ForEach>
				</Collapsible>
			)}
		</Dialog>
	);
}

type SpawnImportUserInstrumentDialogProps = Omit<ImportUserInstrumentDialogProps, "onClose" | "show">;
function spawnImportUserInstrumentDialog(props: SpawnImportUserInstrumentDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<ImportUserInstrumentDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onImportFinished={async () => {
					await props.onImportFinished?.();
					resolve();
				}}
			/>
		)),
	);
}
